import { z } from 'zod'
import { Answer, Event } from './answer.js'
import { Snapshot } from './generatie.js'
import { InstantieId, RevisieId, SectionId, FieldId } from './id.js'
import { Instantie } from './instantie.js'
import { Laag } from './laag.js'
import { Moment, Person } from './moment.js'
import { Source } from './source.js'
import { Thread } from './thread.js'

/**
 * Een revisie is een bevroren stand van de gegevens bij een overdracht
 * (R-95, ontwerp 4.5). Append-only: elke overdracht sluit er één af.
 *
 * Een revisie bewaart twee dingen naast elkaar, en dat is met opzet. Ten eerste
 * per sectie een Yjs-snapshot: klein, en de sleutel tot de attributie en de
 * verschilweergave (6.9). Ten tweede de volledige uitgeschreven stand:
 * zelfstandig leesbaar, zonder Yjs, zonder het schema van toen. Dat tweede is de
 * kern van wat een revisie voor NWO is, want een ingediende aanvraag is een
 * bewijsstuk dat over jaren nog leesbaar moet zijn.
 *
 * Geen delta's en geen apart wijzigingsmodel (R-178a): omdat de vorige en de
 * huidige revisie allebei compleet zijn, is de markering per gewijzigde vraag een
 * berekening in de engine en geen opslagvraagstuk.
 */

/**
 * Een snapshot betekent alleen iets tegen het document waaruit hij is genomen,
 * dus hij draagt de generatie van dat document mee (ontwerp 4.6); zonder dat
 * nummer is hij later niet te renderen.
 */
export const SectionSnapshot = z.strictObject({
  laag: Laag,
  sectieId: SectionId,
  generatie: z.number().int().positive(),
  /** Het binaire Yjs-snapshot, base64 in JSON. De store bewaart hem binair. */
  snapshot: Snapshot,
})
export type SectionSnapshot = z.infer<typeof SectionSnapshot>

/**
 * De bronnen van één sectie zoals de lezer ze zag, bij het bevriezen vastgelegd (R-50, R-202).
 *
 * **Waarom naast de snapshot en niet eruit af te leiden.** De snapshot is een Yjs-snapshot, een
 * statusvector met de verwijderingen, en geen documentstand: zonder het document erbij zijn de
 * bronnen er niet uit te halen. En de vermelding zoals de lezer haar ziet, `(Voorbeeld, 2026)`,
 * komt uit citeproc in de renderer, dat de opslagdienst niet heeft. Het bericht aan het
 * processysteem draagt beide (`koppelvlak.md` hoofdstuk 3), dus de pod legt ze vast terwijl hij
 * het document nog heeft.
 *
 * `bronnen` is de literatuurlijst: de geciteerde bronnen in de volgorde van de lijst, elk met
 * de regel zoals de lijst hem toont, als gesaniteerde HTML. Een bron in de bronnenopslag die
 * nergens wordt geciteerd staat niet in de lijst en hoort niet bij het antwoord. `vermeldingen`
 * is per `citatieId` de vermelding als platte tekst.
 *
 * **Niet op `Revisie` zelf.** `Revisie` is een `strictObject`, en een pod die op een ouder model
 * is bevroren weigert een sleutel die hij niet kent (D-36, de vondst van B4b bij migratie 0023).
 * De opslagdienst neemt dit daarom aan in het revisieverzoek en bewaart het in een eigen kolom,
 * maar geeft het niet terug in de revisie.
 */
export const SectionSources = z.strictObject({
  laag: Laag,
  sectieId: SectionId,
  bronnen: z.array(z.strictObject({ bron: Source, weergave: z.string().min(1) })),
  vermeldingen: z.record(z.string().min(1), z.string().min(1)),
})
export type SectionSources = z.infer<typeof SectionSources>

/**
 * De opmerkingdraadjes van één sectie zoals ze bij het bevriezen in het document stonden, open en
 * opgelost (R-73, R-86, R-95; opmerkingen van november 2026).
 *
 * **Waarom naast de snapshot.** De bevroren stand van een gesloten beurt komt uit de snapshot van de
 * revisie, en daarin staan de draadjes. Valt die stand terug op de antwoorden van de revisie (geen
 * bruikbare snapshot, en geen levende generatie die er gelijk aan is), dan bouwt de pod het document uit
 * de uitgeschreven stand op, en daarin staan geen draadjes: het gesprek, het bewijs waarom er iets is
 * gewijzigd, was dan weg. Met deze lijst zaait de pod ze terug. Een draadje draagt zijn citaat, dus het
 * blijft leesbaar ook als het anker in de opnieuw opgebouwde tekst er niet meer is.
 *
 * **Niet op `Revisie` zelf**, om dezelfde reden als {@link SectionSources}: de opslagdienst neemt de lijst
 * aan in het revisieverzoek, bewaart haar in een eigen kolom (migratie 0044) en geeft haar alleen langs een
 * eigen pad terug. Een revisie van vóór die kolom heeft er geen; dan blijft het zoals het was. Het bericht
 * aan het processysteem draagt de draadjes niet: het gesprek tussen medeschrijvers is geen gegeven van het
 * dossier.
 */
export const SectionThreads = z.strictObject({
  laag: Laag,
  sectieId: SectionId,
  draden: z.array(Thread),
})
export type SectionThreads = z.infer<typeof SectionThreads>

export const Revisie = z
  .strictObject({
    id: RevisieId,
    instantieId: InstantieId,
    /** Oplopend per instantie, zodat de lijn van R-95 een ordening heeft. */
    volgnummer: z.number().int().positive(),
    gebeurtenis: Event,
    moment: Moment,
    persoon: Person,
    /**
     * Namens wie er is gehandeld, of null (R-191). Machtigen zelf hoort bij de
     * gastheer (T15, R-190); het formulier neemt de bevoegdheid over en legt haar
     * vast.
     */
    namens: Person.nullable(),
    /** De volledige uitgeschreven stand, per stabiel veld-id en nooit per pad (ontwerp 9.1). */
    antwoorden: z.record(FieldId, Answer),
    /** Per sectie een snapshot met zijn generatie (ontwerp 4.5, 4.6). */
    snapshots: z.array(SectionSnapshot),
  })
  .superRefine((revisie, ctx) => {
    if (revisie.namens === revisie.persoon) {
      ctx.addIssue({
        code: 'custom',
        path: ['namens'],
        // Namens zichzelf handelen is gewoon zelf handelen; dan hoort er null te staan.
        message: 'namens is null of iemand anders dan persoon',
      })
    }
    const double = new Set<string>()
    revisie.snapshots.forEach((s, i) => {
      const key = `${s.laag}/${s.sectieId}`
      if (double.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['snapshots', i],
          message: `twee snapshots voor sectie ${s.sectieId} in laag ${s.laag}`,
        })
      }
      double.add(key)
    })
  })

export type Revisie = z.infer<typeof Revisie>

/**
 * Wat een overdracht heeft opgeleverd (ontwerp 7.8 voorstel 1).
 *
 * Een overdracht doet twee dingen in één transactie: de revisie afsluiten en het outbox-event
 * schrijven. Alleen de eerste is hier te zien; het event gaat naar buiten en niet terug naar de
 * aanroeper.
 *
 * **`opvolger` is sinds M9 opdracht 2 altijd `null`.** Tot die opdracht deed een overdracht nog
 * een derde ding in dezelfde transactie: de volgende instantie in de lijn klaarzetten, en dat
 * stond hier in het antwoord. Op 9 september 2026 is die derde stap teruggedraaid: het
 * processysteem maakt de opvolger nu zelf aan, via een eigen aanroep van `POST /v1/instanties`
 * (ontwerp 4.5, 7.8). Het veld blijft bestaan (het bevroren contract van `apps/store` staat geen
 * verwijdering toe) en draagt zijn brede type nog steeds, maar de opslagdienst vult er nooit meer
 * iets anders in dan `null`.
 */
export const HandoverOutcome = z.strictObject({
  revisie: Revisie,
  opvolger: Instantie.nullable(),
})
export type HandoverOutcome = z.infer<typeof HandoverOutcome>
